import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { PageSidesheetActions } from '@next/core-lib/components';
import { LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { select, Store } from '@ngrx/store';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';

import {
  ActiveIngredientsSettingState,
  StateActiveIngredientSetting,
} from '../../activeingredientsetting.model';
import * as ActiveIngredientSettingActions from '../../store/actions/activeingredientsetting.actions';
import { getActiveIngredientSettings } from '../../store/selectors/activeingredientsetting.selectors';

@Component({
  selector: 'app-activeingredientsetting',
  templateUrl: './activeingredientsetting.component.html',
  styleUrls: ['./activeingredientsetting.component.scss'],
  standalone: false,
})
export class ActiveIngredientSettingsComponent implements OnInit {
  private readonly store = inject<Store<ActiveIngredientsSettingState>>(Store);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private readonly locale = inject(LocaleService);

  paginator = viewChild(MatPaginator);
  currentYear = new Date().getFullYear();
  availableCropYears = Array.from({ length: this.currentYear + 2 - 2020 }, (_, k) => k + 2020);
  filterNameControl = new FormControl();
  filterYearControl = new FormControl();

  private readonly filterName = toSignal(
    this.filterNameControl.valueChanges.pipe(
      debounceTime(100),
      distinctUntilChanged(),
      map((value: string) => value || ''),
      startWith(''),
    ),
    { initialValue: '' },
  );
  private readonly filterYear = toSignal(
    this.filterYearControl.valueChanges.pipe(
      debounceTime(100),
      distinctUntilChanged(),
      map((value: string) => Number(value) || (null as number)),
      startWith(null as number),
    ),
    { initialValue: null as number },
  );

  activeIngredientSettings = toSignal(
    this.store.pipe(select(getActiveIngredientSettings), distinctUntilChanged()),
    { initialValue: [] as StateActiveIngredientSetting[] },
  );

  filterValue = computed(() => ({
    ingredientName: this.filterName(),
    calendarYear: this.filterYear(),
  }));

  filteredActiveIngredientSettings = computed(() => {
    const settings = this.activeIngredientSettings();
    if (!settings.length) return settings;
    const value = this.filterValue();
    return settings.filter(({ calendarYear, ingredientName }) => {
      const hasNoIngredientNameFilter = !value.ingredientName;
      const hasNoCalendarYearFilter = !value.calendarYear;
      const hasNoFilters = hasNoIngredientNameFilter && hasNoCalendarYearFilter;
      const ingredredientNameIndex = ingredientName
        ?.toLowerCase()
        ?.indexOf(value.ingredientName?.toLowerCase());
      const hasIngredientNameMatch =
        ingredredientNameIndex !== -1 && ingredredientNameIndex !== null;
      const hasCalendarYearMatch = calendarYear === value.calendarYear;
      return (
        hasNoFilters ||
        (hasNoCalendarYearFilter && hasIngredientNameMatch) ||
        (hasNoIngredientNameFilter && hasCalendarYearMatch) ||
        (hasIngredientNameMatch && hasCalendarYearMatch)
      );
    });
  });

  filterEnabled = computed(() => !!this.filterValue());

  pageSize = 10;
  page = signal<PageEvent>({
    pageIndex: 0,
    pageSize: this.pageSize,
    length: null,
  });

  pagedActiveIngredientSettings = computed(() => {
    const settings = this.filteredActiveIngredientSettings();
    const { pageIndex, pageSize } = this.page();
    return settings.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize);
  });

  isMobile = toSignal(this.breakpoint.ltLg$, { initialValue: false });
  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource(),
    selectColumn: false,
    pagination: false,
    header: true,
    footer: false,
    disableCards: true,
    columns: [
      {
        id: 'ingredientId',
        label: this.locale.translate(`id`),
        type: TableColumnTypes.NUMBER,
        disableFormatting: true,
      },
      {
        id: 'ingredientName',
        label: this.locale.translate(`ingredientName`),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'calendarYear',
        label: this.locale.translate(`year`),
        type: TableColumnTypes.NUMBER,
        disableFormatting: true,
      },
      {
        id: 'malusPoints',
        label: this.locale.translate(`totalMalusPoints`),
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'includeInSum',
        label: this.locale.translate(`includeInSum`),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'forbidden',
        label: this.locale.translate(`forbidden`),
        type: TableColumnTypes.STRING,
      },
    ],
    actions: [
      {
        id: 'edit',
        label: this.locale.translate('editActiveIngredientSetting'),
        icon: 'edit',
        callback: (event: StateActiveIngredientSetting) => this.editActiveIngredientSetting(event),
        disabled: () => false,
      },
      {
        id: 'delete',
        label: this.locale.translate('deleteActiveIngredientSetting'),
        icon: 'delete',
        callback: (event: StateActiveIngredientSetting) =>
          this.deleteActiveIngredientSetting(event),
        disabled: () => false,
      },
    ],
  };
  constructor() {
    effect(() => {
      this.filterValue();
      untracked(() => this.resetPagination());
    });

    effect(() => {
      const settings = this.pagedActiveIngredientSettings();
      this.tableConfig.dataSource.data = settings.map((activeIngredientSetting) => ({
        ...activeIngredientSetting,
        includeInSum: activeIngredientSetting.includeInSum ? this.locale.translate('yes') : '',
        forbidden: activeIngredientSetting.forbidden ? this.locale.translate('forbidden') : '',
      }));
    });
  }

  ngOnInit() {
    this.store.dispatch(ActiveIngredientSettingActions.loadActiveIngredientSettingsRequest());
  }

  addActiveIngredientSetting() {
    this.store.dispatch(ActiveIngredientSettingActions.addActiveIngredientSetting());
  }

  editActiveIngredientSetting(activeIngredientSetting: StateActiveIngredientSetting) {
    this.store.dispatch(
      ActiveIngredientSettingActions.editActiveIngredientSetting({ activeIngredientSetting }),
    );
  }

  deleteActiveIngredientSetting(activeIngredientSetting: StateActiveIngredientSetting) {
    this.store.dispatch(
      ActiveIngredientSettingActions.deleteActiveIngredientSetting({ activeIngredientSetting }),
    );
  }

  setSearch() {
    this.store.dispatch(PageSidesheetActions.openSidesheet());
  }

  resetFilter() {
    this.filterNameControl.reset();
    this.filterYearControl.reset();
  }

  onPage(event: PageEvent) {
    this.page.set(event);
  }

  private resetPagination() {
    const p = this.paginator();
    if (p) {
      p.pageIndex = 0;
    }
    this.page.set({
      pageIndex: 0,
      pageSize: this.pageSize,
      length: null,
    });
  }
}
